import {
  AdditiveBlending,
  DoubleSide,
  Mesh,
  MeshBasicMaterial,
  PlaneGeometry,
  RingGeometry,
  SphereGeometry,
  Vector3
} from 'three';
import { Ability, AbilityPhase } from './Ability.js';
import { createRiftGateMaterial, createRiftPathMaterial } from '../materials/RiftMaterial.js';
import { ParticleShape } from '../particles/ParticleSystem.js';
import { RateEmitter } from '../particles/ParticleEngine.js';
import { DecalType } from '../effects/GroundDecals.js';
import { BurstMode } from '../effects/BurstSphere.js';
import { LAYER } from '../core/Layers.js';
import { frame } from '../core/FrameUniforms.js';
import { settings } from '../config/settings.js';
import { getColor } from '../utils/color.js';
import { Easing, saturate } from '../utils/math.js';

const MAX_ECHOES = 12;

const _emit = {};
const _pos = new Vector3();
const _target = new Vector3();
const _direction = new Vector3();

/**
 * BACKFLOW RIFT — a cast with a second, reversed beat.
 *
 * The outbound motion is deliberately quiet: it plants a seed and records a
 * path. At the far end the seed opens a gate, and the dangerous pulse travels
 * back toward the caster. This gives the ability a timing identity and a useful
 * combat purpose: threaten a retreat lane, then catch anyone who commits to it
 * during the return window.
 *
 * Unlike the line abilities, the front is not the whole effect. `u` describes
 * the setup flight; `_returnProgress` describes the real event after impact.
 * The two coordinates stay separate so the editor can freeze either beat.
 */
export class BackflowAbility extends Ability {
  constructor(context) {
    super('backflow', context);
  }

  /* ------------------------------------------------------------------ */
  /* Construction                                                        */
  /* ------------------------------------------------------------------ */

  createShaders() {
    this.pathGeometry = new PlaneGeometry(1, 1, 1, 1);
    this.pathMaterial = createRiftPathMaterial();
    this.pathMesh = new Mesh(this.pathGeometry, this.pathMaterial);
    this.pathMesh.name = 'Backflow path';
    this.pathMesh.frustumCulled = false;
    this.pathMesh.renderOrder = 12;
    this.pathMesh.layers.set(LAYER.VFX);

    this.gateGeometry = new PlaneGeometry(2, 2, 1, 1);
    this.gateMaterial = createRiftGateMaterial();
    this.gateMesh = new Mesh(this.gateGeometry, this.gateMaterial);
    this.gateMesh.name = 'Backflow gate';
    this.gateMesh.rotation.x = -Math.PI / 2;
    this.gateMesh.frustumCulled = false;
    this.gateMesh.renderOrder = 18;
    this.gateMesh.layers.set(LAYER.VFX);
    this.gateMesh.visible = false;

    // A crisp outer edge makes the gate readable against the procedural floor.
    this.echoGeometry = new RingGeometry(0.22, 0.28, 48);
    this.echoRings = [];
    this.echoMaterials = [];
    for (let i = 0; i < MAX_ECHOES; i++) {
      const material = new MeshBasicMaterial({
        color: 0xffffff,
        transparent: true,
        opacity: 0,
        depthWrite: false,
        depthTest: true,
        blending: AdditiveBlending,
        side: DoubleSide,
        toneMapped: false
      });
      const ring = new Mesh(this.echoGeometry, material);
      ring.name = `Backflow echo ${i}`;
      ring.rotation.x = -Math.PI / 2;
      ring.renderOrder = 16;
      ring.layers.set(LAYER.VFX);
      ring.visible = false;
      this.group.add(ring);
      this.echoRings.push(ring);
      this.echoMaterials.push(material);
    }

    this.seedGeometry = new SphereGeometry(0.14, 16, 10);
    this.seedMaterial = new MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 1,
      depthWrite: false,
      depthTest: true,
      blending: AdditiveBlending,
      toneMapped: false
    });
    this.seedMesh = new Mesh(this.seedGeometry, this.seedMaterial);
    this.seedMesh.name = 'Backflow seed';
    this.seedMesh.renderOrder = 20;
    this.seedMesh.layers.set(LAYER.VFX);
    this.group.add(this.seedMesh);

    this.group.add(this.pathMesh, this.gateMesh);

    this._seed = 0;
    this._returnProgress = -1;
    this._returned = false;
    this._gateOpen = 0;
    this._fade = 1;
  }

  createParticles() {
    const particles = this.ctx.particles;

    this.motes = particles.get('backflow.motes', {
      capacity: 3200,
      shape: ParticleShape.SOFT,
      additive: true,
      curl: true,
      swirl: true,
      softFade: 0.35
    });
    this.motes.uniforms.uDrag.value = 1.45;
    this.motes.uniforms.uEndSize.value = 0.12;
    this.motes.uniforms.uSizeIn.value = 0.02;
    this.motes.uniforms.uFadeIn.value = 0.04;
    this.motes.uniforms.uFadeOut.value = 0.45;

    this.fragments = particles.get('backflow.fragments', {
      capacity: 1400,
      shape: ParticleShape.CHIP,
      additive: true,
      lit: false,
      softFade: 0.2
    });
    this.fragments.uniforms.uDrag.value = 0.35;
    this.fragments.uniforms.uEndSize.value = 0.45;
    this.fragments.uniforms.uSizeIn.value = 0.04;
    this.fragments.uniforms.uFadeIn.value = 0.02;
    this.fragments.uniforms.uFadeOut.value = 0.62;

    this.ringParticles = particles.get('backflow.rings', {
      capacity: 700,
      shape: ParticleShape.RING,
      additive: true,
      softFade: 0.3
    });
    this.ringParticles.uniforms.uDrag.value = 0.5;
    this.ringParticles.uniforms.uEndSize.value = 1.0;
    this.ringParticles.uniforms.uSizeIn.value = 0.05;
    this.ringParticles.uniforms.uFadeIn.value = 0.03;
    this.ringParticles.uniforms.uFadeOut.value = 0.65;

    this.moteEmitter = new RateEmitter();
    this.fragmentEmitter = new RateEmitter();
    this.ringEmitter = new RateEmitter();
  }

  /* ------------------------------------------------------------------ */
  /* Timing and shared state                                             */
  /* ------------------------------------------------------------------ */

  get instanceCount() {
    return 1 + Math.min(MAX_ECHOES, Math.max(0, Math.round(this.config.echoCount)));
  }

  get impactDuration() {
    return Math.max(0.08, this.config.returnTime);
  }

  get fadeDuration() {
    return Math.max(0.05, this.config.collapseTime);
  }

  lightShimmer() {
    const c = this.config;
    const step = Math.floor(this.age * Math.max(1, c.lightFlickerSpeed));
    const hash = Math.abs(Math.sin(step * 91.7 + this._seed * 0.13) * 43758.5453) % 1;
    return 1 - c.lightFlicker * hash;
  }

  _pointOnPath(s, out) {
    const c = this.config;
    const t = saturate(s);
    this.pointAt(t, out);
    out.y = c.pathLift + c.pathArch * Math.sin(t * Math.PI);
    return out;
  }

  _syncParticles() {
    const c = this.config;
    const g = settings.global;

    this.motes.setGradient(
      getColor(c.colorMoteA),
      getColor(c.colorMoteB),
      getColor(c.colorMoteC),
      getColor(c.colorMoteD)
    );
    this.motes.uniforms.uGravity.value.set(0, c.moteGravity, 0);
    this.motes.uniforms.uSizeScale.value = c.moteSize * g.particleSize * 7;
    this.motes.uniforms.uLifeScale.value = c.moteLifetime * g.particleLifetime;
    this.motes.uniforms.uSpeedScale.value = c.moteSpeed * g.particleSpeed;
    this.motes.uniforms.uOpacity.value = g.opacity;
    this.motes.uniforms.uGlow.value = 1.1 * g.glow;
    this.motes.uniforms.uTurbulence.value = c.moteTurbulence * g.turbulence;

    this.fragments.setGradient(
      getColor(c.colorFragmentA),
      getColor(c.colorFragmentB),
      getColor(c.colorFragmentC),
      getColor(c.colorFragmentD)
    );
    this.fragments.uniforms.uGravity.value.set(0, c.fragmentGravity, 0);
    this.fragments.uniforms.uSizeScale.value = c.fragmentSize * g.particleSize * 6;
    this.fragments.uniforms.uLifeScale.value = c.fragmentLifetime * g.particleLifetime;
    this.fragments.uniforms.uSpeedScale.value = c.fragmentSpeed * g.particleSpeed;
    this.fragments.uniforms.uOpacity.value = g.opacity;
    this.fragments.uniforms.uGlow.value = 0.8 * g.glow;

    this.ringParticles.setGradient(
      getColor(c.colorEcho),
      getColor(c.colorB),
      getColor(c.colorA),
      getColor(c.colorGate)
    );
    this.ringParticles.uniforms.uGravity.value.set(0, 0, 0);
    this.ringParticles.uniforms.uSizeScale.value = c.ringSize * g.particleSize * 3;
    this.ringParticles.uniforms.uLifeScale.value = c.ringLifetime * g.particleLifetime;
    this.ringParticles.uniforms.uSpeedScale.value = c.ringSpeed * g.particleSpeed;
    this.ringParticles.uniforms.uOpacity.value = g.opacity;
    this.ringParticles.uniforms.uGlow.value = 1.2 * g.glow;
  }

  _syncPath(fade) {
    const c = this.config;
    const g = settings.global;
    const u = this.pathMaterial.uniforms;

    u.uOrigin.value.copy(this.origin);
    u.uTarget.value.copy(this.pointAt(1, _target));
    u.uSide.value.copy(this.side);
    u.uWidth.value = c.pathWidth;
    u.uLift.value = c.pathLift;
    u.uArch.value = c.pathArch;
    u.uTravelProgress.value = this.phase === AbilityPhase.TRAVEL ? this.u : 1;
    u.uReturnProgress.value = this._returnProgress;
    u.uPulseWidth.value = c.pulseWidth;
    u.uPulseIntensity.value = c.pulseIntensity;
    u.uMemoryOpacity.value = c.memoryOpacity;
    u.uFade.value = fade;
    u.uOpacity.value = c.opacity * g.opacity;
    u.uSeed.value = this._seed;
    u.uColorCore.value.copy(getColor(c.colorCore));
    u.uColorA.value.copy(getColor(c.colorA));
    u.uColorB.value.copy(getColor(c.colorB));

    this._syncParticles();
  }

  _syncGate(open, fade) {
    const c = this.config;
    const g = settings.global;
    const target = this.pointAt(1, _target);

    this.gateMesh.position.set(target.x, c.gateHeight, target.z);
    const size = c.gateRadius * (0.18 + 0.82 * saturate(open));
    this.gateMesh.scale.setScalar(Math.max(0.001, size));
    this.gateMesh.visible = open > 0.001 && fade > 0.001;

    const u = this.gateMaterial.uniforms;
    u.uSpin.value = c.gateSpin;
    u.uPulse.value = c.gatePulse;
    u.uPulseSpeed.value = c.gatePulseSpeed;
    u.uOpen.value = open;
    u.uFade.value = fade;
    u.uGlow.value = c.gateGlow * g.glow;
    u.uOpacity.value = c.gateOpacity * c.opacity * g.opacity;
    u.uSeed.value = this._seed;
    u.uColorCore.value.copy(getColor(c.colorCore));
    u.uColorGate.value.copy(getColor(c.colorGate));
    u.uColorEcho.value.copy(getColor(c.colorEcho));
  }

  _syncEchoes() {
    const c = this.config;
    const returning = this._returnProgress >= 0;
    const count = Math.min(MAX_ECHOES, Math.max(0, Math.round(c.echoCount)));
    const wave = returning ? 1 - this._returnProgress : 0;

    for (let i = 0; i < this.echoRings.length; i++) {
      const ring = this.echoRings[i];
      const material = this.echoMaterials[i];
      const visible = returning && i < count;
      const t = wave + i * c.echoSpacing;

      if (!visible || t < 0 || t > 1.02) {
        ring.visible = false;
        material.opacity = 0;
        continue;
      }

      this._pointOnPath(t, _pos);
      ring.position.set(_pos.x, c.pathLift + 0.012, _pos.z);
      ring.scale.setScalar(c.echoSize * (1 - i / Math.max(1, count) * 0.4));
      ring.rotation.z = frame.uTime.value * c.echoSpin + i * 0.7;
      material.color.copy(getColor(c.colorEcho));
      material.opacity = this._fade * (1 - i / Math.max(1, count) * 0.65) * c.opacity * settings.global.opacity;
      ring.visible = material.opacity > 0.002;
    }
  }

  /* ------------------------------------------------------------------ */
  /* Particles and feedback                                              */
  /* ------------------------------------------------------------------ */

  _emitAt(position, dt, scale, returning) {
    const c = this.config;
    const g = settings.global;
    const time = frame.uTime.value;

    let moteCount = Math.round(this.moteEmitter.tick(dt, c.moteRate * scale) * g.particleCount);
    if (moteCount > 0) {
      _emit.position = position;
      _emit.radius = c.pathWidth * (returning ? 1.5 : 0.8) + 0.08;
      _emit.direction = _direction
        .copy(returning ? this.direction : this.side)
        .multiplyScalar(returning ? -0.3 : 0.1)
        .setY(0.8)
        .normalize();
      _emit.speed = c.moteSpeed;
      _emit.speedVariance = 0.8;
      _emit.spread = returning ? 0.5 : 1.0;
      _emit.size = c.moteSize;
      _emit.sizeVariance = 0.7;
      _emit.life = c.moteLifetime;
      _emit.lifeVariance = 0.45;
      _emit.spin = 1.2;
      _emit.anchor = position;
      _emit.inherit = null;
      _emit.time = time;
      this.motes.emit(moteCount, _emit);
    }

    let fragmentCount = Math.round(this.fragmentEmitter.tick(dt, c.fragmentRate * scale) * g.particleCount);
    if (fragmentCount > 0) {
      _emit.position = position;
      _emit.radius = c.pathWidth * 0.7 + 0.04;
      _emit.direction = _direction
        .copy(returning ? this.direction : this.side)
        .multiplyScalar(returning ? -0.6 : 0.25)
        .setY(0.8)
        .normalize();
      _emit.speed = c.fragmentSpeed;
      _emit.speedVariance = 0.7;
      _emit.spread = 0.65;
      _emit.size = c.fragmentSize;
      _emit.sizeVariance = 0.6;
      _emit.life = c.fragmentLifetime;
      _emit.lifeVariance = 0.35;
      _emit.spin = 7;
      _emit.time = time;
      this.fragments.emit(fragmentCount, _emit);
    }

    let ringCount = Math.round(this.ringEmitter.tick(dt, c.ringRate * scale) * g.particleCount);
    if (ringCount > 0) {
      _emit.position = position;
      _emit.radius = 0.03;
      _emit.direction = _direction.set(0, 1, 0);
      _emit.speed = c.ringSpeed;
      _emit.speedVariance = 0.25;
      _emit.spread = 0.08;
      _emit.size = c.ringSize * 0.45;
      _emit.sizeVariance = 0.3;
      _emit.life = c.ringLifetime;
      _emit.lifeVariance = 0.25;
      _emit.spin = 0.5;
      _emit.time = time;
      this.ringParticles.emit(ringCount, _emit);
    }
  }

  _spawnGatePulse(position, intensity = 1) {
    const c = this.config;
    const g = settings.global;
    const time = frame.uTime.value;

    _emit.position = position;
    _emit.radius = 0.15;
    _emit.direction = _direction.set(0, 1, 0);
    _emit.speed = c.ringSpeed * 1.4;
    _emit.speedVariance = 0.25;
    _emit.spread = 0.08;
    _emit.size = c.ringSize;
    _emit.sizeVariance = 0.18;
    _emit.life = c.ringLifetime;
    _emit.lifeVariance = 0.2;
    _emit.spin = 0.8;
    _emit.time = time;
    this.ringParticles.emit(Math.round(28 * intensity * g.particleCount), _emit);
  }

  _spawnMuzzle() {
    const c = this.config;
    const g = settings.global;
    this._pointOnPath(0, _pos);
    this.ctx.bursts.spawn(BurstMode.AIR, _pos, {
      radius: 0.16,
      endRadius: 0.8 * g.explosionIntensity,
      life: 0.28,
      intensity: 1.1,
      opacity: 0.8,
      fresnel: 1.8,
      displace: 0.22,
      colorA: getColor(c.colorA),
      colorB: getColor(c.colorB),
      colorC: getColor(c.colorCore)
    });
  }

  /* ------------------------------------------------------------------ */
  /* Lifecycle                                                           */
  /* ------------------------------------------------------------------ */

  onSpawn() {
    this.moteEmitter.reset();
    this.fragmentEmitter.reset();
    this.ringEmitter.reset();
    this._seed = Math.random() * 100;
    this._returnProgress = -1;
    this._returned = false;
    this._gateOpen = 0;
    this._fade = 1;

    this.seedMesh.visible = true;
    this._spawnMuzzle();
    this._syncPath(1);
    this._syncGate(0, 1);
    this._syncEchoes();
    this._syncSeed(1);
  }

  _syncSeed(alpha) {
    const c = this.config;
    if (this.phase !== AbilityPhase.TRAVEL || alpha <= 0.001) {
      this.seedMesh.visible = false;
      return;
    }

    this._pointOnPath(this.u, _pos);
    this.seedMesh.position.set(_pos.x, _pos.y + 0.12, _pos.z);
    const pulse = 0.85 + 0.18 * Math.sin(frame.uTime.value * 8.0 + this._seed);
    this.seedMesh.scale.setScalar(pulse * (0.75 + this.u * 0.35));
    this.seedMaterial.color.copy(getColor(c.colorCore));
    this.seedMaterial.opacity = alpha * c.opacity * settings.global.opacity;
    this.seedMesh.rotation.y = frame.uTime.value * 4.0;
    this.seedMesh.visible = true;
  }

  onTravel(dt) {
    this._returnProgress = -1;
    this._fade = 1;
    this._syncPath(1);
    this._syncGate(0, 1);
    this._syncEchoes();
    this._syncSeed(1);

    this._pointOnPath(this.u, this.position);
    this._emitAt(this.position, dt, 1, false);
    this.ctx.shake.rumble(0.018 * settings.global.cameraShake, dt);
  }

  onImpact() {
    const c = this.config;
    const g = settings.global;

    this._returnProgress = 0;
    this._gateOpen = 0.08;
    this._fade = 1;
    this.seedMesh.visible = false;
    this._pointOnPath(1, _target);
    this.position.copy(_target);
    this.position.y = c.gateHeight;

    this.ctx.bursts.spawn(BurstMode.AIR, _target, {
      radius: c.gateRadius * 0.18,
      endRadius: c.gateRadius * 0.95 * g.explosionIntensity,
      life: 0.65,
      intensity: 1.35,
      opacity: 0.9,
      fresnel: 2.2,
      displace: 0.35,
      colorA: getColor(c.colorGate),
      colorB: getColor(c.colorB),
      colorC: getColor(c.colorCore)
    });

    this.ctx.decals.spawn(DecalType.RIPPLE, _target, {
      radius: c.gateRadius,
      life: c.returnTime + c.collapseTime,
      growth: 0.18,
      width: 0.08,
      intensity: 0.65,
      colorA: getColor(c.colorGround),
      colorB: getColor(c.colorEcho),
      height: 0.018
    });

    this._spawnGatePulse(_target, 1.5);
    this.ctx.flash.trigger(getColor(c.colorFlash), c.impactFlash * g.explosionIntensity);
    this.ctx.shake.add(c.impactShake * g.explosionIntensity * g.cameraShake, 1 / Math.max(0.1, c.shakeDuration), 20);
    this.lightBoost = c.lightIntensity * 1.2 * g.explosionIntensity;

    this._syncPath(1);
    this._syncGate(this._gateOpen, 1);
    this._syncEchoes();
  }

  _returnBurst() {
    const c = this.config;
    const g = settings.global;
    this._pointOnPath(0, _pos);
    this.ctx.bursts.spawn(BurstMode.AIR, _pos, {
      radius: c.gateRadius * 0.12,
      endRadius: c.gateRadius * 0.62 * g.explosionIntensity,
      life: 0.42,
      intensity: 1.05,
      opacity: 0.75,
      fresnel: 1.8,
      displace: 0.2,
      colorA: getColor(c.colorA),
      colorB: getColor(c.colorGate),
      colorC: getColor(c.colorCore)
    });
    this._spawnGatePulse(_pos, 1.2);
    this.ctx.flash.trigger(getColor(c.colorFlash), c.impactFlash * 0.55 * g.explosionIntensity);
    this.ctx.shake.add(c.returnShake * g.explosionIntensity * g.cameraShake, 1 / Math.max(0.1, c.shakeDuration), 15);
    this.lightBoost = c.lightIntensity * 0.9 * g.explosionIntensity;
  }

  onFade(dt, t) {
    const c = this.config;
    const returning = t <= 1;

    if (returning) {
      this._returnProgress = saturate(t);
      this._gateOpen = Easing.outCubic(saturate(t / 0.22));
      this._fade = 1;
      this._pointOnPath(1 - this._returnProgress, this.position);
      this._emitAt(this.position, dt, 0.9, true);

      if (t >= 1 && !this._returned) {
        this._returned = true;
        this._returnBurst();
      }
    } else {
      const collapse = saturate(t - 1);
      this._returnProgress = 1;
      this._gateOpen = 1 - Easing.inCubic(collapse);
      this._fade = 1 - Easing.inCubic(collapse);
      this._pointOnPath(0, this.position);
      this._emitAt(this.position, dt, this._fade * 0.25, true);
    }

    this._syncPath(this._fade);
    this._syncGate(this._gateOpen, this._fade);
    this._syncEchoes();
    this.seedMesh.visible = false;
  }

  onDestroy() {
    this.seedMesh.visible = false;
    this.gateMesh.visible = false;
    this.pathMaterial.uniforms.uFade.value = 0;
    for (const ring of this.echoRings) ring.visible = false;
  }

  dispose() {
    this.pathGeometry.dispose();
    this.pathMaterial.dispose();
    this.gateGeometry.dispose();
    this.gateMaterial.dispose();
    this.echoGeometry.dispose();
    for (const material of this.echoMaterials) material.dispose();
    this.seedGeometry.dispose();
    this.seedMaterial.dispose();
    super.dispose();
  }
}
